import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReelCommentSheet from './ReelCommentSheet';
import MediaRender from '../../../components/MediaRender';

// ─── Animated heart ───────────────────────────────────────────────────────────
const HeartBurst = ({ active }) => (
  <span
    className="material-symbols-outlined transition-all duration-300 select-none"
    style={{
      fontSize: '28px',
      color: active ? '#ff4d6d' : 'rgba(255,255,255,0.85)',
      fontVariationSettings: active ? "'FILL' 1" : "'FILL' 0",
      transform: active ? 'scale(1.25)' : 'scale(1)',
      filter: active ? 'drop-shadow(0 0 8px #ff4d6d88)' : 'none',
    }}
  >
    favorite
  </span>
);

// ─── Action button ────────────────────────────────────────────────────────────
const ActionBtn = ({ icon, label, onClick, active, color }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center gap-1 group"
    style={{ minWidth: '44px' }}
  >
    <div
      className="w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 group-active:scale-90"
      style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)' }}
    >
      {icon === 'favorite' ? (
        <HeartBurst active={active} />
      ) : (
        <span
          className="material-symbols-outlined"
          style={{
            fontSize: '24px',
            color: color || 'rgba(255,255,255,0.85)',
          }}
        >
          {icon}
        </span>
      )}
    </div>
    {label !== undefined && (
      <span className="text-white text-[11px] font-semibold" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
        {label}
      </span>
    )}
  </button>
);

// ─── Progress bar ─────────────────────────────────────────────────────────────
const VideoProgress = ({ videoRef, duration }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => {
      const d = video.duration || duration || 1;
      setProgress((video.currentTime / d) * 100);
    };
    video.addEventListener('timeupdate', onTime);
    return () => video.removeEventListener('timeupdate', onTime);
  }, [videoRef, duration]);

  return (
    <div className="absolute top-0 left-0 right-0 h-0.5 z-20" style={{ background: 'rgba(255,255,255,0.15)' }}>
      <div
        className="h-full transition-none"
        style={{
          width: `${progress}%`,
          background: 'linear-gradient(90deg,#c9a96e,#e8c98a)',
        }}
      />
    </div>
  );
};

// ─── Single reel item ─────────────────────────────────────────────────────────
const ReelItem = ({
  reel,
  isActive,
  currentUser,
  onLike,
  onAddComment,
  onShare,
}) => {
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(reel.likesCount || 0);
  const [showComments, setShowComments] = useState(false);
  const [captionExpanded, setCaptionExpanded] = useState(false);
  const [doubleTapHeart, setDoubleTapHeart] = useState(false);
  const lastTapRef = useRef(0);

  // Sync liked state from reel data
  useEffect(() => {
    if (currentUser) {
      const alreadyLiked = reel.likes?.some((l) => l.userId === currentUser.id);
      setLiked(alreadyLiked || false);
    }
    setLikesCount(reel.likesCount || 0);
  }, [reel, currentUser]);

  // Auto play/pause based on visibility
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isActive) {
      video.play().catch(() => {});
      setPlaying(true);
    } else {
      video.pause();
      video.currentTime = 0;
      setPlaying(false);
    }
  }, [isActive]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const now = Date.now();
    // Double-tap = like
    if (now - lastTapRef.current < 300) {
      handleLike();
      setDoubleTapHeart(true);
      setTimeout(() => setDoubleTapHeart(false), 900);
      return;
    }
    lastTapRef.current = now;

    if (video.paused) {
      video.play().catch(() => {});
      setPlaying(true);
    } else {
      video.pause();
      setPlaying(false);
    }
  }, []);

  const handleLike = useCallback(() => {
    if (!currentUser) return;
    const nowLiked = !liked;
    setLiked(nowLiked);
    setLikesCount((c) => c + (nowLiked ? 1 : -1));
    onLike(reel.id);
  }, [liked, currentUser, onLike, reel.id]);

  const handleAddComment = useCallback(async (text) => {
    if (!currentUser) return;
    await onAddComment(reel.id, text);
  }, [currentUser, onAddComment, reel.id]);

  const captionLong = reel.caption && reel.caption.length > 80;
  const captionDisplay = captionLong && !captionExpanded
    ? reel.caption.slice(0, 80) + '…'
    : reel.caption;

  return (
    <div
      className="relative w-full flex-shrink-0"
      style={{ height: '100svh', background: '#000', scrollSnapAlign: 'start' }}
    >
      {/* Progress bar */}
      <VideoProgress videoRef={videoRef} duration={reel.duration} />

      {/* Video */}
      <MediaRender
        type="video"
        ref={videoRef}
        src={reel.videoUrl}
        className="absolute inset-0 w-full h-full object-cover"
        loop
        playsInline
        muted={muted}
        preload="metadata"
        poster={reel.thumbnailUrl}
        onClick={togglePlay}
        style={{ cursor: 'pointer' }}
      />

      {/* Dark gradient overlays */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 40%, rgba(0,0,0,0.2) 100%)' }}
      />

      {/* Double-tap heart */}
      {doubleTapHeart && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
          <span
            className="material-symbols-outlined animate-ping"
            style={{ fontSize: '80px', color: '#ff4d6d', fontVariationSettings: "'FILL' 1" }}
          >
            favorite
          </span>
        </div>
      )}

      {/* Pause indicator */}
      {!playing && isActive && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}
          >
            <span className="material-symbols-outlined text-white text-[36px]">play_arrow</span>
          </div>
        </div>
      )}

      {/* ── Right action rail ── */}
      <div
        className="absolute right-3 bottom-28 flex flex-col items-center gap-5 z-20"
      >
        {/* Author avatar */}
        <div className="relative mb-2 cursor-pointer transition-transform hover:scale-105" onClick={() => navigate(`/user-profile?id=${reel.author?.id}`)}>
          <img
            src={reel.author?.avatarUrl || 'https://images.unsplash.com/photo-1611695434398-4f4b330623e6'}
            alt={reel.author?.fullName}
            className="w-11 h-11 rounded-full object-cover ring-2"
            style={{ ringColor: '#c9a96e' }}
          />
          <div
            className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg,#c9a96e,#e8c98a)' }}
          >
            <span className="material-symbols-outlined text-black text-[12px]">add</span>
          </div>
        </div>

        {/* Like */}
        <ActionBtn
          icon="favorite"
          label={likesCount > 999 ? `${(likesCount / 1000).toFixed(1)}k` : likesCount}
          active={liked}
          onClick={handleLike}
        />

        {/* Comment */}
        <ActionBtn
          icon="chat_bubble_outline"
          label={reel.commentsCount || 0}
          onClick={() => setShowComments(true)}
        />

        {/* Share */}
        <ActionBtn
          icon="share"
          onClick={() => onShare(reel)}
        />

        {/* Mute */}
        <ActionBtn
          icon={muted ? 'volume_off' : 'volume_up'}
          onClick={() => setMuted((m) => !m)}
        />
      </div>

      {/* ── Bottom info ── */}
      <div className="absolute bottom-0 left-0 right-16 p-5 z-20">
        {/* Author */}
        <div className="flex items-center gap-2 mb-2">
          <span 
            className="text-white font-bold text-sm cursor-pointer hover:underline"
            onClick={() => navigate(`/user-profile?id=${reel.author?.id}`)}
          >
            @{reel.author?.username}
          </span>
          <span className="text-white/40 text-xs">·</span>
          <span className="text-white/50 text-xs">
            {new Date(reel.createdAt).toLocaleDateString()}
          </span>
        </div>

        {/* Caption */}
        {reel.caption && (
          <p className="text-white/90 text-sm leading-relaxed mb-3">
            {captionDisplay}
            {captionLong && (
              <button
                onClick={() => setCaptionExpanded((v) => !v)}
                className="text-white/50 ml-1 text-xs font-semibold"
              >
                {captionExpanded ? 'less' : 'more'}
              </button>
            )}
          </p>
        )}

        {/* Audio ticker */}
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            <span className="material-symbols-outlined text-white text-[14px]" style={{ fontVariationSettings: "'FILL' 1" }}>music_note</span>
          </div>
          <div className="overflow-hidden flex-1" style={{ maxWidth: '200px' }}>
            <p
              className="text-white text-xs font-semibold whitespace-nowrap"
              style={{
                animation: 'ticker 8s linear infinite',
              }}
            >
              ♪ {reel.audioName}
            </p>
          </div>
        </div>
      </div>

      {/* Comments */}
      {showComments && (
        <ReelCommentSheet
          reel={reel}
          currentUser={currentUser}
          onClose={() => setShowComments(false)}
          onAddComment={handleAddComment}
        />
      )}
    </div>
  );
};

export default ReelItem;
